import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import { Phone, ArrowLeft, CheckCircle2, ShieldCheck, Thermometer, Wind, Wrench, Sparkles, Clock, ArrowRight, Zap, Laptop, Send } from 'lucide-react';
import Footer from '../components/Footer';
import HeaderSocials from '../components/HeaderSocials';
import InstagramVideoSection from '../components/InstagramVideoSection';
import LandingFaqSchema from '../components/LandingFaqSchema';
import useLandingContent from '../hooks/useLandingContent';
import LaptopCleaningCalculator from '../components/LaptopCleaningCalculator';

const ACCENT = 'from-cyan-400 to-blue-500';
const GLOW = 'shadow-[0_0_80px_rgba(59,130,246,0.45)]';
const GRAD = `bg-gradient-to-r ${ACCENT} bg-clip-text text-transparent`;

const FEATURES = [
  'Повне розбирання корпусу зі зняттям нижньої кришки',
  'Чистка кулера, радіатора та теплових трубок від пилу',
  'Заміна термопасти преміум-класу (Arctic MX-6 / Thermal Grizzly)',
  'Заміна термопрокладок VRAM/VRM за необхідності',
  'Змазка підшипника кулера, усунення сторонніх шумів',
  'Профілактика портів, клавіатури та системи живлення'
];

const STEPS = [
  { title: 'Безкоштовна діагностика', desc: 'Заміряємо температури, слухаємо кулер, оглядаємо ноутбук. Даємо чесний висновок — чи потрібна чистка.', img: '/images/generated/laptop-step-1.png', icon: ShieldCheck },
  { title: 'Розбирання', desc: 'Демонтуємо нижню кришку, від\'єднуємо акумулятор. Все відбувається в антистатичних умовах на професійному робочому місці.', img: '/images/generated/laptop-step-2.png', icon: Wrench },
  { title: 'Показуємо що всередині', desc: 'Фото «до» надсилаємо у Telegram. Ви побачите весь пил у кулері, що заважає охолодженню процесора та відеокарти.', img: '/images/generated/laptop-step-3.png', icon: Sparkles },
  { title: 'Глибока чистка', desc: 'Стисненим повітрям, щітками та розчинами видаляємо весь пил з радіатора, кулера та плати.', img: '/images/generated/laptop-step-4.png', icon: Wind },
  { title: 'Заміна термопасти', desc: 'Наносимо преміум-термопасту на CPU та GPU, оновлюємо термопрокладки за потреби.', img: '/images/generated/laptop-step-5.png', icon: Thermometer },
  { title: 'Тест під навантаженням', desc: 'Прогріваємо у стрес-тесті, стежимо за температурами й throttling. Показуємо результат «до і після».', img: '/images/generated/laptop-step-1.png', icon: Zap }
];

const SIGNS = [
  { icon: '🔥', text: 'Ноутбук перегрівається та вимикається сам' },
  { icon: '🌀', text: 'Кулер гуде на максимумі постійно' },
  { icon: '🐢', text: 'Тормозить, зависає, падає FPS в іграх' },
  { icon: '🥵', text: 'Корпус і клавіатура гарячі на дотик' },
  { icon: '⚡', text: 'Раптово вимикається під навантаженням' },
  { icon: '📅', text: 'Не чистили більше 12 місяців' }
];

const FAQS = [
  { q: 'Скільки коштує чистка ноутбука?', a: 'Звичайний ноутбук — від 700 грн. Ігрові ноутбуки — від 1500 грн. Точну ціну назве майстер після безкоштовної діагностики.' },
  { q: 'Скільки триває чистка?', a: 'Звичайний ноутбук — 1–2 години. Ігровий або складний у розбиранні — до 3 годин. Можна зачекати або залишити на кілька годин.' },
  { q: 'Яку термопасту використовуєте?', a: 'Преміум-класу — Arctic MX-6 або Thermal Grizzly. Вони тримають температуру значно краще за стокову, яка з часом висихає.' },
  { q: 'Мої дані в безпеці?', a: 'Так. Ми працюємо лише із «залізом» — накопичувач, дані та ОС не чіпаємо. Все залишається на місці.' },
  { q: 'Даєте гарантію?', a: 'Так, офіційна гарантія 30 днів на виконані роботи. Діагностика — безкоштовна.' }
];

const TYPES = {
  regular: { label: 'Звичайний', price: 700, duration: '1–2 год', note: 'Ноутбуки для роботи й навчання — ASUS, Acer, Lenovo, HP, Dell та ін.' },
  gaming: { label: 'Ігровий', price: 1500, duration: '2–3 год', note: 'Ігрові ноутбуки з потужним охолодженням — ROG, Legion, Predator, Nitro та ін.' }
};

const LaptopCleaningLandingPage = () => {
  const [active, setActive] = useState('regular');
  const landing = useLandingContent('laptops');
  const t = TYPES[active];
  return (
    <div className="min-h-screen console-page" data-testid="laptop-cleaning-page">
      <Helmet>
        <title>Чистка ноутбуків в Одесі — від пилу + заміна термопасти | CIMM Computers</title>
        <meta name="description" content="Професійна чистка ноутбуків в Одесі: розбирання, чистка системи охолодження від пилу, заміна термопасти преміум-класу. Усуваємо перегрів, шум кулера та throttling. Від 700 грн. Гарантія 30 днів. ☎ +38 097 417 8788" />
        <meta name="keywords" content="чистка ноутбука Одеса, чистка ноутбуків Одеса, заміна термопасти ноутбук Одеса, перегрів ноутбука, ноутбук шумить чистка, профілактика ноутбука Одеса" />
        <link rel="canonical" href="https://cimm.od.ua/chistka-noutbukiv" />
        <meta property="og:title" content="Чистка ноутбуків в Одесі — CIMM Computers" />
        <meta property="og:description" content="Чистка від пилу та заміна термопасти. Усуваємо перегрів і шум кулера. Від 700 грн, гарантія 30 днів." />
        <meta property="og:image" content="https://cimm.od.ua/images/generated/laptop-hero.png" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://cimm.od.ua/chistka-noutbukiv" />
        <script type="application/ld+json">{JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'Service',
          serviceType: 'Чистка ноутбуків та заміна термопасти',
          provider: {
            '@type': 'LocalBusiness',
            name: 'CIMM Computers',
            telephone: '+380974178788',
            address: { '@type': 'PostalAddress', addressLocality: 'Одеса', addressCountry: 'UA' }
          },
          areaServed: { '@type': 'City', name: 'Одеса' },
          offers: [{ '@type': 'Offer', name: 'Чистка ноутбука', price: '700', priceCurrency: 'UAH' }]
        })}</script>
      </Helmet>
      <LandingFaqSchema category="laptops" />

      {/* Header */}
      <header className="cyber-header fixed top-0 left-0 right-0 z-50">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="header-home-link">
            <div className="text-2xl font-bold neon-text">CIMM</div>
            <div className="text-sm text-gray-400 hidden md:block">Комп'ютери та Ремонт</div>
          </Link>
          <nav className="hidden md:flex items-center gap-6">
            <Link to="/" className="text-gray-300 hover:text-cyan-400 transition-all">Головна</Link>
            <Link to="/phones" className="text-gray-300 hover:text-cyan-400 transition-all">Телефони</Link>
            <Link to="/laptops" className="text-gray-300 hover:text-cyan-400 transition-all">Ноутбуки</Link>
            <Link to="/chistka-macbook" className="text-gray-300 hover:text-cyan-400 transition-all">Чистка MacBook</Link>
            <Link to="/contacts" className="text-gray-300 hover:text-cyan-400 transition-all">Контакти</Link>
          </nav>
          <div className="flex items-center gap-2">
            <HeaderSocials />
            <a href="tel:+380974178788" className="neon-text font-semibold" data-testid="header-phone-link">+38 097 417 8788</a>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative min-h-[92vh] md:min-h-[88vh] flex items-center overflow-hidden pt-28 pb-16 md:pt-24 md:pb-20 console-hero">
        <div className="absolute inset-0 pointer-events-none console-hero-bg" aria-hidden="true">
          <div className="absolute inset-0" style={{ background: 'radial-gradient(ellipse 70% 90% at 75% 50%, rgba(59,130,246,0.25), transparent 65%)' }} />
        </div>

        <div className="absolute inset-y-0 right-0 w-full md:w-[62%] lg:w-[58%] pointer-events-none overflow-hidden">
          <img src="/images/generated/laptop-hero.png" alt="Чистка ноутбука" className="w-full h-full object-contain object-right" data-testid="laptop-hero-img" />
          <div className="absolute inset-y-0 left-0 w-1/2 console-hero-fade" />
        </div>

        <div className="container mx-auto max-w-7xl px-4 relative z-10">
          <div className="flex flex-col items-start mb-8">
            <Link to="/" className="inline-flex items-center gap-2 hero-back-link mb-6 transition-colors text-sm" data-testid="back-home-link">
              <ArrowLeft className="w-4 h-4" /> На головну
            </Link>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-cyan-500/10 border border-cyan-500/30 mb-5 backdrop-blur-sm">
              <Laptop className="w-3.5 h-3.5 text-cyan-400" />
              <span className="text-xs uppercase tracking-widest text-cyan-400">Чистка ноутбуків · Одеса</span>
            </div>
            <div className="inline-flex p-1.5 rounded-full console-switcher-bg backdrop-blur-xl border shadow-2xl" data-testid="laptop-type-switcher">
              {Object.entries(TYPES).map(([k, v]) => (
                <button
                  key={k}
                  onClick={() => setActive(k)}
                  data-testid={`switcher-${k}`}
                  className={`px-5 md:px-7 py-2.5 rounded-full text-sm md:text-base font-semibold transition-all duration-500 ${
                    active === k ? `bg-gradient-to-r ${ACCENT} text-white shadow-lg` : 'hero-switcher-idle'
                  }`}
                >
                  {v.label}
                </button>
              ))}
            </div>
          </div>

          <div className="max-w-2xl">
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-black leading-[1.02] mb-5 hero-h1">
              Чистка <span className={GRAD}>{active === 'gaming' ? 'ігрового' : ''} ноутбука</span>
              <br />в Одесі
            </h1>
            <p className="text-lg md:text-xl mb-6 max-w-xl hero-lead">
              {t.note} Прибираємо пил, міняємо термопасту, знижуємо температури в середньому на <span className="font-bold hero-lead-strong">−20°C</span>. Термін від <span className="font-bold hero-lead-strong">{t.duration}</span>. Гарантія <span className="font-bold hero-lead-strong">30 днів</span>.
            </p>

            <div className="flex flex-wrap items-baseline gap-3 mb-8">
              <div className="hero-price-from text-sm">від</div>
              <div className={`text-6xl md:text-7xl font-black ${GRAD}`} data-testid="laptop-hero-price">{t.price}</div>
              <div className="hero-price-unit text-2xl font-bold">грн</div>
            </div>

            <div className="flex flex-wrap gap-3">
              <a href="tel:+380974178788" data-testid="hero-cta-call" className={`inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r ${ACCENT} text-white font-bold rounded-full transition-all duration-300 hover:scale-105 ${GLOW}`}>
                <Phone className="w-5 h-5" /> Записатись зараз
              </a>
              <a href="#steps" data-testid="hero-cta-details" className="inline-flex items-center gap-2 px-8 py-4 hero-cta-secondary font-semibold rounded-full border transition-all">
                Як ми це робимо <ArrowRight className="w-4 h-4" />
              </a>
            </div>

            <div className="flex flex-wrap gap-6 mt-8 text-sm hero-meta">
              <div className="flex items-center gap-2"><Clock className="w-4 h-4 text-cyan-400" /> від {t.duration}</div>
              <div className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-cyan-400" /> Гарантія 30 днів</div>
              <div className="flex items-center gap-2"><Sparkles className="w-4 h-4 text-cyan-400" /> Діагностика 0 грн</div>
            </div>
          </div>

          <div className="absolute right-4 md:right-10 top-1/2 -translate-y-1/2 z-10 hidden lg:block">
            <div className="glass-card px-5 py-4 backdrop-blur-2xl border text-center rotate-3 hero-effect-badge">
              <div className="text-[10px] uppercase tracking-widest hero-effect-label">Ефект</div>
              <div className={`text-4xl font-black ${GRAD}`}>−20°C</div>
              <div className="text-xs hero-effect-sub mt-1">після чистки</div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-14 md:py-20 px-4">
        <div className="container mx-auto max-w-6xl">
          <div className="glass-card p-6 md:p-10 backdrop-blur-2xl bg-white/[0.03] border border-white/10">
            <div className="grid md:grid-cols-3 gap-6">
              <div className="md:col-span-1">
                <div className="text-xs uppercase tracking-widest text-cyan-400 mb-2">Що входить</div>
                <h3 className="text-2xl md:text-3xl font-black text-white leading-tight">Повний профілактичний комплекс</h3>
                <p className="text-gray-400 text-sm mt-3">Все включено у ціну. Ніяких прихованих доплат.</p>
              </div>
              <ul className="md:col-span-2 grid sm:grid-cols-2 gap-3">
                {FEATURES.map((f, i) => (
                  <li key={i} className="flex items-start gap-3 text-gray-300">
                    <CheckCircle2 className="w-5 h-5 flex-shrink-0 mt-0.5 text-cyan-400" />
                    <span className="text-sm md:text-base">{f}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Gaming thermal materials explainer */}
      {active === 'gaming' && (
        <section className="py-14 md:py-20 px-4" data-testid="gaming-thermal-info">
          <div className="container mx-auto max-w-6xl">
            <div className="glass-card p-6 md:p-10 border border-cyan-500/20">
              <div className="text-xs uppercase tracking-widest text-cyan-400 mb-2">Чому ігровий дорожче</div>
              <h2 className="text-2xl md:text-4xl font-black text-white mb-4">Ігрові ноутбуки — відновлюємо охолодження як із заводу</h2>
              <p className="text-gray-300 md:text-lg mb-8 max-w-3xl">
                У потужних ігрових ноутбуках виробник з заводу закладає <span className="text-cyan-400 font-semibold">преміальні термоінтерфейси</span>, а не звичайну термопасту. Тому обслуговування складніше й дорожче — ми відновлюємо саме той тип охолодження, який передбачений конструкцією, щоб не втратити продуктивність і гарантійні температури.
              </p>
              <div className="grid md:grid-cols-3 gap-4">
                {[
                  { t: 'Рідкий метал', d: 'На топових моделях ASUS ROG і Legion відновлюємо заводський рідкий метал на CPU/GPU з ізоляцією компонентів.' },
                  { t: 'Фазовий перехід PTM7950', d: 'Використовуємо оригінальний фазоперехідний матеріал, який розкриває теплопередачу під ігровим навантаженням.' },
                  { t: 'Заводські термопрокладки', d: 'Підбираємо товщину та тип термопрокладок для VRAM і VRM, щоб відновити передбачене виробником охолодження.' }
                ].map((x, i) => (
                  <div key={i} className="bg-black/20 rounded-2xl p-5 border border-white/10">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-r from-cyan-400 to-blue-500 flex items-center justify-center mb-3">
                      <Thermometer className="w-5 h-5 text-white" />
                    </div>
                    <h3 className="text-white font-bold mb-2">{x.t}</h3>
                    <p className="text-gray-400 text-sm leading-relaxed">{x.d}</p>
                  </div>
                ))}
              </div>
              <p className="text-gray-400 text-sm mt-6">Ціна від 1500 грн залежить від типу термоінтерфейсу, складності розбирання та кількості зон охолодження. Після діагностики майстер погодить вартість і використає саме той матеріал, який передбачив виробник.</p>
            </div>
          </div>
        </section>
      )}

      {active === 'gaming' && <LaptopCleaningCalculator />}

      {/* Signs */}
      <section className="py-14 md:py-20 px-4">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-12">
            <div className="text-xs uppercase tracking-widest text-cyan-400 mb-2">Симптоми</div>
            <h2 className="text-3xl md:text-5xl font-black text-white">Коли ноутбук пора на <span className={GRAD}>чистку</span></h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {SIGNS.map((s, i) => (
              <div key={i} className="glass-card p-5 hover:border-cyan-500/50 transition-all" data-testid={`sign-${i}`}>
                <div className="text-3xl mb-2">{s.icon}</div>
                <p className="text-gray-300 text-sm md:text-base">{s.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Steps */}
      <section id="steps" className="py-14 md:py-24 px-4">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-14">
            <div className="text-xs uppercase tracking-widest text-cyan-400 mb-2">Процес</div>
            <h2 className="text-3xl md:text-5xl font-black text-white mb-4">6 етапів <span className={GRAD}>ідеальної чистки</span></h2>
            <p className="text-gray-400 max-w-2xl mx-auto">Прозоро від першого дзвінка до тестування. Ми фотографуємо кожен етап і надсилаємо вам у Telegram.</p>
          </div>
          <div className="space-y-8 md:space-y-16">
            {STEPS.map((s, i) => {
              const Icon = s.icon;
              const reverse = i % 2 === 1;
              return (
                <div key={i} className={`grid md:grid-cols-2 gap-6 md:gap-10 items-center ${reverse ? 'md:[direction:rtl]' : ''}`} data-testid={`step-${i}`}>
                  <div className="md:[direction:ltr]">
                    <div className="rounded-2xl overflow-hidden border border-white/10 relative aspect-[4/3] bg-black/40">
                      <img src={s.img} alt={s.title} className="w-full h-full object-cover" loading="lazy" />
                      <div className="absolute top-3 left-3 w-10 h-10 rounded-full bg-black/70 backdrop-blur flex items-center justify-center text-cyan-400 font-black text-lg border border-cyan-500/40">
                        {String(i + 1).padStart(2, '0')}
                      </div>
                    </div>
                  </div>
                  <div className="md:[direction:ltr]">
                    <div className={`inline-flex items-center gap-2 mb-3 px-3 py-1.5 rounded-full bg-gradient-to-r ${ACCENT}`}>
                      <Icon className="w-4 h-4 text-white" />
                      <span className="text-xs uppercase tracking-wider text-white font-semibold">Крок {i + 1}</span>
                    </div>
                    <h3 className="text-2xl md:text-3xl font-black text-white mb-3">{s.title}</h3>
                    <p className="text-gray-300 text-base md:text-lg leading-relaxed">{s.desc}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Price + trust */}
      <section className="py-14 md:py-20 px-4">
        <div className="container mx-auto max-w-6xl grid md:grid-cols-2 gap-8 items-stretch">
          <div className="glass-card p-8 flex flex-col justify-between">
            <div>
              <div className="text-xs uppercase tracking-widest text-cyan-400 mb-3">Прайс</div>
              <h3 className="text-3xl font-black text-white mb-6">Ціна на чистку ноутбука</h3>
              <div className="flex items-baseline gap-3 mb-6">
                <div className="text-gray-400">від</div>
                <div className={`text-7xl font-black ${GRAD}`} data-testid="laptop-price-value">{t.price}</div>
                <div className="text-gray-400 text-2xl">грн</div>
              </div>
              <ul className="space-y-2 text-gray-300 text-sm">
                <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-cyan-400 flex-shrink-0 mt-0.5" /> Звичайний ноутбук — від 700 грн</li>
                <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-cyan-400 flex-shrink-0 mt-0.5" /> Ігровий ноутбук — від 1500 грн</li>
                <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-cyan-400 flex-shrink-0 mt-0.5" /> Термопаста преміум-класу входить у ціну</li>
                <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-cyan-400 flex-shrink-0 mt-0.5" /> Діагностика — 0 грн, гарантія 30 днів</li>
              </ul>
            </div>
            <a href="tel:+380974178788" data-testid="price-cta-call" className={`mt-8 inline-flex items-center justify-center gap-2 px-6 py-4 bg-gradient-to-r ${ACCENT} text-white font-bold rounded-full text-lg ${GLOW}`}>
              <Phone className="w-5 h-5" /> Записатись за 30 сек
            </a>
          </div>
          <div className="glass-card p-8 space-y-6">
            <div>
              <div className="text-xs uppercase tracking-widest text-cyan-400 mb-3">Чому нас</div>
              <h3 className="text-3xl font-black text-white">Обирають в Одесі</h3>
            </div>
            <div className="space-y-4">
              {[
                { n: '8+', l: 'років ремонтуємо ноутбуки' },
                { n: '5000+', l: 'ноутбуків пройшло через наші руки' },
                { n: '−20°C', l: 'середнє зниження температури' },
                { n: '30 днів', l: 'офіційної гарантії' }
              ].map((s, i) => (
                <div key={i} className="flex items-center gap-4">
                  <div className={`text-3xl md:text-4xl font-black ${GRAD} min-w-[100px]`}>{s.n}</div>
                  <div className="text-gray-300">{s.l}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <InstagramVideoSection
        url={active === 'gaming' ? landing?.instagram_video_gaming : landing?.instagram_video_regular}
        accentClass="text-cyan-400"
      />

      {/* FAQ */}
      <section className="py-14 md:py-20 px-4">
        <div className="container mx-auto max-w-4xl">
          <div className="text-center mb-12">
            <div className="text-xs uppercase tracking-widest text-cyan-400 mb-2">FAQ</div>
            <h2 className="text-3xl md:text-5xl font-black text-white">Часті питання</h2>
          </div>
          <div className="space-y-3" data-testid="faq-section">
            {FAQS.map((f, i) => (
              <details key={i} className="glass-card p-6 group cursor-pointer" data-testid={`faq-${i}`}>
                <summary className="flex items-center justify-between text-white font-semibold list-none">
                  <span className="pr-4">{f.q}</span>
                  <span className="text-cyan-400 text-2xl group-open:rotate-45 transition-transform">+</span>
                </summary>
                <p className="text-gray-400 mt-4 leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-14 md:py-24 px-4">
        <div className="container mx-auto max-w-4xl">
          <div className="glass-card p-8 md:p-14 text-center relative overflow-hidden">
            <div className="relative z-10">
              <h2 className="text-3xl md:text-5xl font-black text-white mb-4">Ваш ноутбук заслуговує <span className={GRAD}>тиші</span></h2>
              <p className="text-gray-300 text-lg mb-8 max-w-xl mx-auto">Один дзвінок — і вже сьогодні ви заберете ноутбук, який працює прохолодно й тихо.</p>
              <div className="flex flex-wrap gap-3 justify-center">
                <a href="tel:+380974178788" data-testid="final-cta-call" className={`inline-flex items-center gap-2 px-10 py-4 bg-gradient-to-r ${ACCENT} text-white font-bold rounded-full text-lg ${GLOW}`}>
                  <Phone className="w-5 h-5" /> +38 097 417 8788
                </a>
                <a href="https://t.me/cimmodua" target="_blank" rel="noopener noreferrer" data-testid="final-cta-telegram" className="inline-flex items-center gap-2 px-10 py-4 bg-white/5 hover:bg-white/10 text-white font-semibold rounded-full border border-white/10 transition-all">
                  <Send className="w-5 h-5" /> Написати в Telegram
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default LaptopCleaningLandingPage;
